export type EmployeePublic = {
  id: string;
  slug: string;
  short_code: string;
  full_name: string | null;
  title: string | null;
  company: string;
  department: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  linkedin_url: string | null;
  website_url: string | null;
  company_profile_url: string | null;
  company_linkedin_url: string | null;
  photo_url: string | null;
  photo_focal_x: number;
  photo_focal_y: number;
  photo_zoom: number;
  whatsapp_number: string | null;
  prefilled_text: string | null;
  locale: string;
};

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? "";

export class HttpError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "HttpError";
  }
}

async function getJson<T>(path: string): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`);
  if (!resp.ok) {
    throw new HttpError(resp.status, `HTTP ${resp.status} on ${path}`);
  }
  return (await resp.json()) as T;
}

export function vcardUrl(slug: string): string {
  return `${API_BASE}/vcard/${encodeURIComponent(slug)}.vcf`;
}

export function shortCodeUrl(shortCode: string): string {
  return `${API_BASE}/c/${encodeURIComponent(shortCode)}`;
}

export function getEmployeeBySlug(slug: string): Promise<EmployeePublic> {
  return getJson<EmployeePublic>(`/api/employees/by-slug/${encodeURIComponent(slug)}`);
}

// ---------------- Admin API ----------------
// Admin calls go through the Pages Functions proxy at /api/admin/*,
// which CF Access protects and which forwards the JWT to Railway.
// Same-origin, so no API_BASE prefix — always relative.

export type UserPublic = {
  id: string;
  email: string;
  role: "admin" | "employee";
  employee_id: string | null;
};

export type EmployeeAdmin = EmployeePublic & {
  active: boolean;
  email_intro_subject: string | null;
  email_intro_body: string | null;
  created_at: string;
  updated_at: string;
  // M13 invite status — null means no email was ever set on this employee
  // (no login account exists).
  invite_status: "invited" | "active" | "disabled" | null;
  invited_at: string | null;
  last_login_at: string | null;
};

export type SendTestEmailResponse = {
  message_id: string;
  submitted_at: string;
  to_email: string;
};

export type LeadAdmin = {
  id: string;
  employee_id: string | null;
  employee_slug: string | null;
  employee_name: string | null;
  wa_id: string;
  profile_name: string | null;
  name: string | null;
  title: string | null;
  company: string | null;
  email: string | null;
  mobile: string | null;
  landline: string | null;
  address: string | null;
  city: string | null;
  linkedin_url: string | null;
  website_url: string | null;
  consent: boolean;
  source_tag: string | null;
  notes: string | null;
  status: string;
  has_card_image: boolean;
  created_at: string;
  updated_at: string;
};

/** Path for the authenticated card-image proxy — never a public R2 URL.
 * Same-origin + CF Access cookie means a plain <img src> works here,
 * unlike the /app side which carries its JWT in an Authorization header
 * (see lib/auth.ts authedGetBlobUrl). */
export function adminCardImageUrl(leadId: string): string {
  return `/api/admin/leads/${leadId}/card-image`;
}

export type LeadsPage = {
  items: LeadAdmin[];
  total: number;
  limit: number;
  offset: number;
};

async function adminRequest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const resp = await fetch(`/api/admin${path}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new HttpError(resp.status, text || `HTTP ${resp.status} on ${path}`);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export const adminApi = {
  me: () => adminRequest<UserPublic>("GET", "/me"),
  listEmployees: (includeInactive = true) =>
    adminRequest<EmployeeAdmin[]>(
      "GET",
      `/employees?include_inactive=${includeInactive}`,
    ),
  createEmployee: (payload: Partial<EmployeeAdmin>) =>
    adminRequest<EmployeeAdmin>("POST", "/employees", payload),
  updateEmployee: (id: string, payload: Partial<EmployeeAdmin>) =>
    adminRequest<EmployeeAdmin>("PATCH", `/employees/${id}`, payload),
  deactivateEmployee: (id: string) =>
    adminRequest<void>("DELETE", `/employees/${id}`),
  resendInvite: (id: string) =>
    adminRequest<void>("POST", `/employees/${id}/resend-invite`),
  listLeads: (params: { q?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    qs.set("limit", String(params.limit ?? 50));
    qs.set("offset", String(params.offset ?? 0));
    return adminRequest<LeadsPage>("GET", `/leads?${qs.toString()}`);
  },
  deleteLead: (id: string) => adminRequest<void>("DELETE", `/leads/${id}`),
  updateLead: (id: string, payload: Partial<LeadAdmin>) =>
    adminRequest<LeadAdmin>("PATCH", `/leads/${id}`, payload),
  sendTestEmail: (employeeId: string, to_email: string, to_name?: string) =>
    adminRequest<SendTestEmailResponse>("POST", `/employees/${employeeId}/send-test-email`, {
      to_email,
      to_name,
    }),
};
